import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import { Access, Guest, ParkingTicketAccess } from "src/app/interfaces/access";
import { Observable } from "rxjs";
import { Homeowner } from "src/app/interfaces/user";
import { firestore } from "firebase/app";

@Injectable({
  providedIn: "root",
})
export class AccessService {
  private accessCollection: AngularFirestoreCollection<Access>;
  private access: Observable<Access[]>;
  public eventDetails: {
    guest: Guest;
    access: Access;
    homeowner: Homeowner;
    guestButton: boolean;
    placeInfo: any;
    ticket: ParkingTicketAccess;
    newInterface: boolean;
    location: { lat: number; lng: number } | null;
  } = {
    guest: null,
    access: null,
    homeowner: null,
    guestButton: false,
    placeInfo: null,
    ticket: null,
    newInterface: false,
    location: null,
  };

  public accessData: Access[];
  constructor(private afs: AngularFirestore) {
    const dateYesterday = new Date();
    dateYesterday.setDate(dateYesterday.getDate() - 1);
    this.accessCollection = this.afs.collection<Access>("access");
  }

  accessGet(id: string) {
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref.where("acc", "array-contains", id);
      })
      .get();
  }

  eventGet(id: string) {
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref.where("id", "==", id);
      })
      .get();
  }

  ticketGet(id: string) {
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where("id", "==", id)
          .where("type", "==", "parking")
          .limit(1);
      })
      .doc<Access>(id)
      .valueChanges();
  }

  eventDetailsGet() {
    return this.eventDetails;
  }

  eventDetailsSet(guest: Guest, access: Access) {
    this.eventDetails.guest = guest;
    this.eventDetails.access = access;
  }

  private sanitizeFirestoreData(value: any): any {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.sanitizeFirestoreData(item))
        .filter((item) => item !== undefined);
    }

    if (value && value.constructor === Object) {
      const sanitized = {};
      Object.keys(value).forEach((key) => {
        const cleanValue = this.sanitizeFirestoreData(value[key]);
        if (cleanValue !== undefined) {
          sanitized[key] = cleanValue;
        }
      });
      return sanitized;
    }

    return value;
  }

  saveAccess(access: Access): Promise<void> {
    const cleanAccess = this.sanitizeFirestoreData(access);
    return this.afs.firestore
      .collection("access")
      .doc(access.id)
      .set(cleanAccess);
  }

  updateAccess(access: Access): Promise<void> {
    const cleanAccess = this.sanitizeFirestoreData(access);
    return this.afs.firestore
      .collection("access")
      .doc(access.id)
      .update(cleanAccess);
  }

  deleteAccess(access: Access): Promise<void> {
    access.delete = firestore.Timestamp.fromDate(new Date());
    return this.updateAccess(access);
  }

  generatorDestinationsArrayRefs(
    destinations: string[],
  ): firestore.DocumentReference[] {
    // Mapea cada ID de lugar en el array destinations a una referencia de documento de Firestore
    return destinations.map((placeId) => {
      // Crea una referencia de documento de Firestore para el lugar con el ID correspondiente
      // en la colección 'places'
      return this.afs.firestore.doc(`places/${placeId}`);
    });
  }

  getActiveAccesses(InstallationId): Observable<Access[]> {
    const dateYesterday = new Date();
    dateYesterday.setDate(dateYesterday.getDate() - 1);
    return this.afs
      .collection("access", (ref) =>
        ref
          .where(
            "installationRef",
            "==",
            this.afs.firestore.doc(`installations/${InstallationId}`),
          )
          .where("type", "==", "event")
          .where("date.final", ">", dateYesterday)
          .orderBy("date.final", "asc"),
      )
      .valueChanges() as Observable<Access[]>;
  }

  getAllActiveAccesses(): Observable<Access[]> {
    const dateYesterday = new Date();
    dateYesterday.setDate(dateYesterday.getDate() - 1);
    return this.afs
      .collection("access", (ref) =>
        ref
          .where("type", "==", "event")
          .where("date.final", ">", dateYesterday)
          .orderBy("date.final", "asc"),
      )
      .valueChanges() as Observable<Access[]>;
  }
}
